/**
 * Loading Spinner Component
 * @author Muhammad Arsan
 * @description A reusable Button component,  component using Material-UI's Button component
 */

import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { buttonRadiusClassName } from "@config/theme/button";
import type { ButtonProps, ButtonVariant } from "./types/button";

const variantClasses: Record<ButtonVariant, string> = {
	primary:
		"bg-[var(--tvet-primary,#2a9699)] text-[var(--tvet-primary-foreground,#ffffff)] hover:bg-[var(--tvet-primary-hover,#248a8c)] font-semibold border-0",
	secondary:
		"bg-[#e1f0f5] text-[var(--tvet-primary,#2a9699)] hover:bg-[#d4eaf0] font-semibold border-0",
	icon: "text-black hover:text-[var(--tvet-primary,#2a9699)] p-1",
	tertiary:
		"bg-white font-semibold text-[var(--tvet-primary,#2a9699)] border border-[var(--tvet-primary,#2a9699)] hover:bg-[#f0ffff]",
	danger:
		"bg-danger text-[var(--tvet-danger-foreground,#ffffff)] hover:opacity-90 font-semibold border-0",
	dangerSecondary:
		"bg-danger text-[var(--tvet-danger-foreground,#ffffff)] hover:opacity-90 font-semibold border-0",
	dangerTertiary:
		"bg-white font-semibold text-danger border border-danger hover:bg-danger-soft",
	neutral:
		"bg-white font-semibold text-muted border border-border hover:bg-surface-muted",
};

const Button: React.FC<ButtonProps> = React.memo(
	({
		text,
		variant = "primary",
		loading = false,
		isDisabled = false,
		className = "",
		disabled,
		children,
		...props
	}) => {
		const isButtonDisabled = loading || isDisabled || disabled;

		const baseClasses =
			variant === "icon"
				? "font-sans tracking-wide transition-colors duration-150 flex items-center justify-center"
				: `${buttonRadiusClassName} font-sans tracking-wide px-6 py-3 text-base transition-all duration-200 flex items-center justify-center gap-2 shadow-[0_5px_10px_rgba(0,0,0,0.05)]`;

		const hoverClasses = variant === "icon" ? "" : "hover:shadow-md";
		return (
			<button
				type="button"
				className={`${baseClasses} ${variantClasses[variant]} ${
					isButtonDisabled ? "opacity-60 cursor-not-allowed" : hoverClasses
				} ${className}`}
				disabled={isButtonDisabled}
				{...props}>
				{loading && <CircularProgress size={18} color="inherit" />}
				{children ? children : text}
			</button>
		);
	},
);

export default Button;
